import { RouteType } from "types/RouteType";
import type RootStore from "stores/RootStore";
import {
  createMockStructureCastClientStore,
  createMockStructureCastProjectStore,
  createMockStructureCastStructureStore,
  createMockStructureCastComponentStore,
  createMockStructureCastCastingGroupStore,
  createMockStructureCastCastingDayStore,
  createMockStructureCastMoldAssignmentStore,
  createMockStructureCastShipmentStore,
  createMockStructureCastShipmentLoadStore,
  createMockStructureCastPreProductionMeetingStore,
  createMockStructureCastCastingTableStore,
  createMockStructureCastYardZoneStore,
  createMockStructureCastEstimateStore,
  createMockStructureCastEstimateLineItemStore,
  createMockStructureCastQcInspectionStore,
  createMockStructureCastBatchTicketStore,
  createMockStructureCastRawMaterialStore,
  createMockStructureCastVendorStore,
  createMockStructureCastPurchaseOrderStore,
  createMockStructureCastPurchaseOrderLineItemStore,
} from "serviceStores/mock";
import type StructureCastProductionScheduleStore from "./MillerProductionScheduleStore";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyRootStore = any;

const structureCastProductionScheduleRoute: RouteType = {
  name: "Production Schedule",
  path: "/structurecast-production-schedule",
  layout: "/app",
  viewportCard: {
    header: {
      title: "Casting Schedule",
      enableRefreshButton: true,
      enableBackButton: true,
      component: () => import("./MillerProductionSchedule.Header"),
    },
    body: {
      component: () => import("./MillerProductionSchedule.Body"),
      grayBackground: true,
    },
  },
  group: "Scheduling",
  requiresAuth: true,
  requiresPermission: false,
  dialogs: [
    {
      name: "assignComponent",
      title: "Assign Component to Mold",
      component: () => import("./dialogs/ScheduleItemDialog"),
      height: 500,
    },
    {
      name: "addCastingDay",
      title: "Add Casting Day",
      component: () => import("./dialogs/PushScheduleDialog"),
      height: 400,
    },
    {
      name: "castingDayDetail",
      title: "Piece Details",
      component: () => import("./dialogs/CastingDayDetailDialog"),
      height: 750,
    },
    {
      name: "createEstimate",
      title: "New Estimate",
      component: () => import("./dialogs/CreateEstimateDialog"),
      height: 620,
    },
    {
      name: "addEstimateStructure",
      title: "Add Structure",
      component: () => import("./dialogs/AddEstimateStructureDialog"),
      height: 650,
    },
    {
      name: "editRateSheet",
      title: "Edit Rate Sheet",
      component: () => import("./dialogs/EditRateSheetDialog"),
      height: 520,
    },
    {
      name: "submitEstimate",
      title: "Submit Estimate",
      component: () => import("./dialogs/SubmitEstimateDialog"),
      height: 350,
    },
    {
      name: "approveEstimate",
      title: "Review Estimate",
      component: () => import("./dialogs/ApproveEstimateDialog"),
      height: 400,
    },
    {
      name: "convertEstimate",
      title: "Convert to Production",
      component: () => import("./dialogs/ConvertEstimateDialog"),
      height: 380,
    },
    {
      name: "printLabels",
      title: "Print Piece Labels",
      component: () => import("./dialogs/PrintLabelsDialog"),
      height: 820,
    },
    {
      name: "pushPieces",
      title: "Push Pieces by Mold Column",
      component: () => import("./dialogs/PushPiecesDialog"),
      height: 720,
    },
    {
      name: "logBatchTicket",
      title: "Log Batch Ticket",
      component: () => import("./dialogs/LogBatchTicketDialog"),
      height: 640,
    },
    {
      name: "logCylinderBreak",
      title: "Log Cylinder Break",
      component: () => import("./dialogs/LogCylinderBreakDialog"),
      height: 520,
    },
    {
      name: "vendorForm",
      title: "Vendor",
      component: () => import("./dialogs/VendorFormDialog"),
      height: 720,
    },
    {
      name: "purchaseOrderForm",
      title: "Purchase Order",
      component: () => import("./dialogs/PurchaseOrderFormDialog"),
      height: 820,
    },
  ],
  onEnter: async ({ rootStore }: { rootStore: RootStore }) => {
    const rs = rootStore as AnyRootStore;

    // Initialize mock stores if not already present
    if (!rs.structureCastClientsStore) {
      rs.structureCastClientsStore = createMockStructureCastClientStore();
      rs.structureCastProjectsStore = createMockStructureCastProjectStore();
      rs.structureCastStructuresStore = createMockStructureCastStructureStore();
      rs.structureCastComponentsStore = createMockStructureCastComponentStore();
      rs.structureCastCastingGroupsStore = createMockStructureCastCastingGroupStore();
      rs.structureCastCastingDaysStore = createMockStructureCastCastingDayStore();
      rs.structureCastMoldAssignmentsStore = createMockStructureCastMoldAssignmentStore();
      rs.structureCastShipmentsStore = createMockStructureCastShipmentStore();
      rs.structureCastShipmentLoadsStore = createMockStructureCastShipmentLoadStore();
      rs.structureCastPreProductionMeetingsStore = createMockStructureCastPreProductionMeetingStore();
      rs.structureCastCastingTablesStore = createMockStructureCastCastingTableStore();
      rs.structureCastYardZonesStore = createMockStructureCastYardZoneStore();
      rs.structureCastEstimatesStore = createMockStructureCastEstimateStore();
      rs.structureCastEstimateLineItemsStore = createMockStructureCastEstimateLineItemStore();
      rs.structureCastQcInspectionsStore = createMockStructureCastQcInspectionStore();
      rs.structureCastBatchTicketsStore = createMockStructureCastBatchTicketStore();
      rs.structureCastRawMaterialsStore = createMockStructureCastRawMaterialStore();
      rs.structureCastVendorsStore = createMockStructureCastVendorStore();
      rs.structureCastPurchaseOrdersStore = createMockStructureCastPurchaseOrderStore();
      rs.structureCastPurchaseOrderLineItemsStore = createMockStructureCastPurchaseOrderLineItemStore();
    }

    // Load data
    const store = rs.millerProductionScheduleStore as StructureCastProductionScheduleStore;
    if (store) {
      await store.loadData();
    }
  },
};

export default structureCastProductionScheduleRoute;
